/**
 * 深邃蓝模板 - 首页
 * 风格：深蓝商务 + 卡片化布局
 */

import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { getListings, getSiteCategories, getSiteInfo, ListingItem, FrontendCategory } from "@/utils/api";
import DeepBlueNavbar from "../components/Navbar";
import DeepBlueFooter from "../components/Footer";

export default function HomePage() {
  const { siteConfig } = useSiteConfig();
  const siteDisplayName = siteConfig?.site_name || siteConfig?.name || "";

  const [listings, setListings] = useState<ListingItem[]>([]);
  const [categories, setCategories] = useState<FrontendCategory[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [catLoading, setCatLoading] = useState(true);
  const [searchConfig, setSearchConfig] = useState({ enabled: true, disabledMessage: "" });
  const [showSearchTip, setShowSearchTip] = useState(false);

  useEffect(() => {
    getSiteInfo().then((info) => {
      if (info) {
        if (info.search_enabled !== undefined) {
          setSearchConfig({
            enabled: info.search_enabled !== false,
            disabledMessage: info.search_disabled_message || "搜索功能已关闭",
          });
        }
      }
    });
  }, []);

  useEffect(() => {
    async function load() {
      try {
        const [l, c] = await Promise.all([getListings({ page: 1, page_size: 6 }), getSiteCategories()]);
        setListings(l.list || []);
        const cats = Array.isArray(c) ? c : [];
        setCategories(cats);
      } catch (e) { console.error(e); }
      finally { setLoading(false); setCatLoading(false); }
    }
    load();
  }, []);

  const handleSearch = useCallback(() => {
    if (!searchConfig.enabled) {
      setShowSearchTip(true);
      setTimeout(() => setShowSearchTip(false), 3000);
      return;
    }
    if (searchQuery.trim()) window.location.href = `/category?search=${encodeURIComponent(searchQuery.trim())}`;
  }, [searchQuery, searchConfig]);

  return (
    <div className="min-h-screen bg-gray-50">
      <DeepBlueNavbar />

      {/* Hero */}
      <section className="bg-[#0F1D2F] pt-12 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">发现商业机会，<span className="text-blue-400">触达更多可能</span></h1>
          <p className="text-blue-200/70 text-lg mb-8">{siteDisplayName || "专业的商业信息服务平台"}</p>
          <div className="max-w-xl mx-auto">
            <div className="flex">
              <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="搜索商业服务..." className="flex-1 px-5 py-3.5 bg-white/10 border border-white/20 rounded-l-lg text-sm text-white placeholder-blue-300/50 outline-none focus:bg-white/15" />
              <button onClick={handleSearch} className="px-6 py-3.5 bg-blue-600 text-white rounded-r-lg hover:bg-blue-700 transition-colors">
                <i className="ri-search-line" />
              </button>
            </div>
            {showSearchTip && (
              <p className="mt-2 text-xs text-amber-300 flex items-center gap-1">
                <i className="ri-error-warning-line" />
                {searchConfig.disabledMessage || "搜索功能已关闭"}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* 分类 */}
      <section className="-mt-10 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {catLoading ? (
            <div className="flex justify-center gap-3">
              {[1,2,3,4,5].map(i => <div key={i} className="w-20 h-20 bg-white/60 rounded-xl animate-pulse" />)}
            </div>
          ) : categories.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {categories.slice(0, 10).map((cat) => (
                <Link key={cat.id} to={`/category?cat=${cat.slug}`}
                  className="bg-white rounded-xl p-4 text-center shadow-sm border border-gray-100 hover:shadow-md hover:border-blue-200 transition-all group">
                  <div className="w-10 h-10 mx-auto mb-2 rounded-lg bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <i className="ri-folder-line text-blue-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors">{cat.name}</span>
                </Link>
              ))}
            </div>
          ) : null}
        </div>
      </section>

      {/* 最新信息 - 正方形图片 */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">最新发布</h2>
            <Link to="/category" className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">查看全部 <i className="ri-arrow-right-line" /></Link>
          </div>

          {loading ? (
            <div className="text-center py-16 text-gray-400"><i className="ri-loader-4-line animate-spin text-2xl" /></div>
          ) : listings.length === 0 ? (
            <div className="text-center py-16 text-gray-400"><i className="ri-inbox-line text-4xl mb-2 block" />暂无内容</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {listings.map((item) => (
                <Link key={item.id} to={`/listing/${item.id}`} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md hover:border-blue-200 transition-all group">
                  {item.images?.[0] ? (
                    <img src={item.images[0]} alt={item.title} className="w-full aspect-square object-cover" />
                  ) : (
                    <div className="w-full aspect-square bg-gradient-to-br from-blue-50 to-slate-100 flex items-center justify-center"><i className="ri-image-line text-gray-300 text-3xl" /></div>
                  )}
                  <div className="p-4">
                    {item.category?.name && <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded">{item.category.name}</span>}
                    <h3 className="text-sm font-semibold text-gray-900 mt-2 group-hover:text-blue-600 transition-colors line-clamp-2">{item.title}</h3>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">{item.content}</p>
                    <div className="flex items-center gap-3 mt-3 text-xs text-gray-400">
                      <span><i className="ri-time-line mr-0.5" />{item.created_at}</span>
                      <span><i className="ri-eye-line mr-0.5" />{item.view_count}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <DeepBlueFooter />
    </div>
  );
}
